import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import JSZip from 'jszip';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { composeSignatureAttestation, CONSENT_TEXT_INPUT_TYPES, getSignerRoleLabel, normalizeOverlayText, overlayTextIssue, resolveSignatureMeaning, } from 'libs.consent.common';
import { Button, Input, Link, LocaleContext, ModalWindow, Spinner, Text, useLocalizeMessage, useToastNotification, } from 'libs.nucleus';
import { waitForCompleteDocument } from './wait_for_complete_document';
import { buildConsentCopyPdfBytes, downloadBlob, extractBundlePdfData, printPdfBlob, useSignedPdfUpload, } from '../file_upload';
import { getDeviceClockAttestation } from '../utils/device_clock';
import { invalidateSignedDocumentQueries } from '../utils/invalidate_signed_document_queries';
import { getDisplayNameInitials } from '../utils/name_initials';
import { isDocumentVoidable } from '../utils/signed_document_status.helpers';
import { activeInviteWave, findOwnInvite, isDelayedSigningLocked, isInviteOutOfTurn, isRoleInActiveWave, } from '../utils/signing_turn.helpers';
const ECONSENT_BUNDLE_HOST_MESSAGE_TAG = '__econsent_bundle_host__';
const ECONSENT_BUNDLE_HOST_RESPONSE_TAG = '__econsent_bundle_host_response__';
const BUNDLE_NOT_READY_REFETCH_INTERVAL_MS = 4000;
const DELAYED_SIGNING_REFETCH_INTERVAL_MS = 30000;
// FRS 14.2: wait up to five minutes for the content to be prepared before reporting a failure,
// instead of spinning forever in a participant-facing modal.
//
// Measured as wall-clock elapsed time rather than a count of completed polls. A poll count only
// advances when the response *changes*: TanStack Query's structural sharing hands back the previous
// object when a payload is byte-identical, which is precisely what a stalled bundle returns, so a
// counter driven off the data would sit still exactly when the timeout needs to fire.
const BUNDLE_NOT_READY_TIMEOUT_MS = 300000;
export function participantDocumentRefetchInterval(data, bundleWaitElapsed) {
    if (!data) {
        return false;
    }
    // The bundle poll stops once the file exists. A signer who already has the document open
    // still has to see a later release without leaving and coming back.
    if (isDelayedSigningLocked(data)) {
        return DELAYED_SIGNING_REFETCH_INTERVAL_MS;
    }
    if (bundleWaitElapsed || data.ec__published_studio_template?.ec__bundle?.url || !data.ec__published_studio_template) {
        return false;
    }
    return BUNDLE_NOT_READY_REFETCH_INTERVAL_MS;
}
const DOCUMENT_DETAIL_PATHS = [
    '_id',
    'created',
    'ec__accepted_signers.ec__account',
    'ec__accepted_signers.ec__signer_role',
    'ec__accepted_signers.ec__status',
    'ec__can_sign_document',
    'ec__pts',
    'ec__document_template.ec__title',
    'ec__document_template.ec__requested_data',
    'ec__document_template.ec__requested_signatures',
    'ec__document_template.ec__signer_roles',
    'ec__document_template.ec__delayed_signing',
    'ec__document_template.ec__pts_only',
    'ec__published_studio_template',
    'ec__published_studio_template.ec__bundle',
    'ec__published_studio_template.ec__requested_data',
    'ec__published_studio_template.ec__requested_signatures',
    'ec__published_studio_template.ec__signer_roles',
    // Whole subdocuments (incl. `ec__identifier` + `ec__value`) — the value
    // is needed to pre-check saved choice overlays (SSP-4107).
    'ec__required_data',
    'ec__signatures',
    'ec__status',
    'ec__identifier',
    'ec__signature_invites.ec__email',
    'ec__signature_invites.ec__signer_role',
    'ec__signature_invites.ec__order',
    'ec__signature_invites.ec__status',
];
const TEXT_EXTENSIONS = ['html', 'js', 'css', 'json', 'txt', 'map', 'svg'];
const toArray = (value) => {
    if (Array.isArray(value)) {
        return value;
    }
    return value?.data || [];
};
const getSignerAccountId = (signer) => typeof signer.ec__account === 'string' ? signer.ec__account : signer.ec__account?._id;
const getRuntimeSignatures = (documentDetail) => documentDetail?.ec__published_studio_template?.ec__requested_signatures ||
    documentDetail?.ec__document_template?.ec__requested_signatures ||
    [];
const getRuntimeData = (documentDetail) => documentDetail?.ec__published_studio_template?.ec__requested_data ||
    documentDetail?.ec__document_template?.ec__requested_data ||
    [];
const getRuntimeSignerRoles = (documentDetail) => documentDetail?.ec__published_studio_template?.ec__signer_roles ||
    documentDetail?.ec__document_template?.ec__signer_roles ||
    [];
const getSignedSignatureIdentifiers = (documentDetail) => new Set(toArray(documentDetail?.ec__signatures)
    .map((signature) => signature.value?.ec__signature_identifier)
    .filter(Boolean));
const SIGNATURE_REASON_KEY = 'label.participant_documents.signature_reason';
/** Signing action per stored `ec__sign_method`, carrying the 21 CFR 11.50 reason. */
const SIGNATURE_ACTION_KEYS = {
    credentials: 'label.participant_documents.signature_action.credentials',
    sso: 'label.participant_documents.signature_action.sso',
};
/** Locale key per base signer type. Unknown roles have none and render the stored token. */
const SIGNER_ROLE_LABEL_KEYS = {
    'participant': 'label.participant_documents.signer_role.participant',
    'non-signing participant': 'label.participant_documents.signer_role.non_signing_participant',
    'third party': 'label.participant_documents.signer_role.third_party',
    'internal signer': 'label.participant_documents.signer_role.internal_signer',
};
/**
 * A translator returns the key itself when the locale has no entry. Callers
 * need "no entry" to be distinguishable so they can fall back to the stored
 * token rather than stamping a lookup key onto a legal record.
 */
const resolveKey = (translate, key) => {
    if (!key) {
        return undefined;
    }
    const resolved = translate(key);
    return resolved && resolved !== key ? resolved : undefined;
};
export const getSignedSignatureSummaries = (documentDetail, localizedReason, labels) => {
    const requestedByKey = new Map(getRuntimeSignatures(documentDetail).map((requirement) => [requirement.ec__key, requirement]));
    return toArray(documentDetail?.ec__signatures)
        .map((signature) => {
        const storedRole = signature.value?.ec__signer_role;
        const storedMethod = signature.value?.ec__sign_method;
        // `storedRole` is a per-template custom role key (e.g. "crc"), not one of
        // the four base signer types `labels.roles` is keyed by — resolve it via
        // the template's signer-role map first. Both fallbacks land on the
        // stored token: the role and the action are descriptive, and an
        // untranslated token is a truthful record where a lookup key is not.
        const resolvedRoleType = getSignerTypeForRole(documentDetail, storedRole) || storedRole;
        const roleLabel = labels.roles[resolvedRoleType || ''] ?? (resolvedRoleType ? getSignerRoleLabel(resolvedRoleType) : undefined);
        const action = labels.actions[storedMethod || ''];
        const meaning = resolveSignatureMeaning(signature.value?.ec__reason, localizedReason);
        const identifier = signature.value?.ec__signature_identifier || '';
        const isInitials = !!requestedByKey.get(identifier)?.ec__initials;
        // Classic paints `value.signed_initials` on initials widgets and `signer`
        // on signature widgets. Fall back to `signer` when initials were never stored.
        const overlayName = isInitials
            ? signature.value?.signed_initials?.trim() || signature.signer || ''
            : signature.signer || '';
        return {
            ec__key: identifier,
            name: overlayName,
            id: signature._id,
            date: signature.date,
            meaning,
            attestation: composeSignatureAttestation(roleLabel, action),
            clockSource: signature.value?.ec__clock_source,
            deviceIanaZone: signature.value?.ec__device_iana_zone,
            deviceUtcOffset: signature.value?.ec__device_utc_offset,
            deviceLocalTime: signature.value?.ec__device_local_time,
        };
    })
        .filter((signature) => signature.ec__key && signature.name);
};
const isBlankStoredText = (datum) => {
    if (!datum.type || !CONSENT_TEXT_INPUT_TYPES.includes(datum.type)) {
        return false;
    }
    const value = datum.ec__value;
    return value == null || String(value).trim() === '';
};
const getCompletedDataIdentifiers = (documentDetail) => new Set(toArray(documentDetail?.ec__required_data)
    .filter((datum) => datum.ec__identifier && !isBlankStoredText(datum))
    .map((datum) => datum.ec__identifier)
    .filter(Boolean));
const isConsentTextInputType = (type) => !!type && CONSENT_TEXT_INPUT_TYPES.includes(type);
const COMPLETED_SIGNER_FAULT = 'cannotUpdateCompletedSigner';
const isCannotUpdateCompletedSignerError = (error) => {
    const seen = new Set();
    const visit = (value) => {
        if (typeof value === 'string') {
            return value.includes(COMPLETED_SIGNER_FAULT) || /cannot update a completed signer/i.test(value);
        }
        if (!value || typeof value !== 'object' || seen.has(value)) {
            return false;
        }
        seen.add(value);
        const record = value;
        return [record.message, record.errCode, record.data, record.response, record.error, record.cause, record.body].some(visit);
    };
    return visit(error);
};
/**
 * Resolve a signer *role* to its signer *type* via the template's
 * `ec__signer_roles`. The bundle gates choice/signature overlays by
 * `signerType`, so this maps the current site user's role to the value the
 * bundle compares against (SSP-4107).
 */
const getSignerTypeForRole = (documentDetail, role) => {
    if (!role) {
        return undefined;
    }
    return getRuntimeSignerRoles(documentDetail).find((entry) => entry.ec__role === role)?.ec__signer_type;
};
/** Map each choice `questionKey` to the signer role it's assigned to (least-privilege persistence guard). */
const getRequestedDataRoleByKey = (documentDetail) => {
    const map = new Map();
    for (const def of getRuntimeData(documentDetail)) {
        if (def?.ec__key && def.ec__signer_role) {
            map.set(def.ec__key, def.ec__signer_role);
        }
    }
    return map;
};
/**
 * Parse an `ec__selection_options` string (`key=value` pairs joined by `=,`,
 * key = integer option key per SSP-4085) into a `value → optionKey` map.
 * Mirrors `econsentStudio.service.ts#parseSelectionOptions` and the `=,`
 * split Cortex's `ec__document_processor` does, which is what keeps a comma
 * inside a reference value ("Yes, I agree") from splitting the option.
 */
const parseSelectionOptions = (raw) => {
    const map = new Map();
    if (typeof raw !== 'string' || raw.length === 0) {
        return map;
    }
    for (const pair of raw.split('=,')) {
        const eq = pair.indexOf('=');
        if (eq <= 0) {
            continue;
        }
        const key = Number(pair.slice(0, eq).trim());
        const value = pair.slice(eq + 1).trim();
        if (Number.isInteger(key) && value.length > 0) {
            map.set(value, key);
        }
    }
    return map;
};
/**
 * Project the signed document's saved `ec__text_choice` answers down to
 * `{ questionKey, selectedOptionKeys }` so the bundle can pre-check the
 * participant's radio/checkbox selections on load. Ports
 * `econsentStudio.service.ts#extractCompletedChoiceSummaries` (SSP-4107):
 * saved reference values are mapped back to integer option keys via the
 * template's `ec__selection_options`; legacy comma-joined multi-select
 * strings are split apart; unmatched values are dropped.
 */
export const getSavedChoiceSelections = (documentDetail) => {
    const defs = getRuntimeData(documentDetail);
    const optionKeyByValueByQuestion = new Map();
    const multiByQuestion = new Map();
    for (const def of defs) {
        if (def?.ec__type === 'ec__text_choice' && typeof def.ec__key === 'string' && def.ec__key) {
            optionKeyByValueByQuestion.set(def.ec__key, parseSelectionOptions(def.ec__selection_options));
            multiByQuestion.set(def.ec__key, !!def.ec__allow_multiple);
        }
    }
    const summaries = [];
    const entries = toArray(documentDetail?.ec__required_data);
    for (const entry of entries) {
        const questionKey = entry?.ec__identifier;
        if (typeof questionKey !== 'string' || !questionKey) {
            continue;
        }
        const valueToKey = optionKeyByValueByQuestion.get(questionKey);
        if (!valueToKey) {
            continue;
        }
        const raw = entry.ec__value;
        const rawValues = Array.isArray(raw) ? raw : raw != null ? [raw] : [];
        const allowMultiple = multiByQuestion.get(questionKey) ?? false;
        const values = [];
        for (const value of rawValues) {
            const normalized = typeof value === 'string' ? value.trim() : '';
            if (!normalized) {
                continue;
            }
            // Legacy multi-select may store all selections as one comma-joined
            // string — split it back apart when it isn't itself a known value.
            if (allowMultiple && normalized.includes(',') && !valueToKey.has(normalized)) {
                for (const part of normalized.split(/,(?! )/g)) {
                    const trimmed = part.trim();
                    if (trimmed) {
                        values.push(trimmed);
                    }
                }
            }
            else {
                values.push(normalized);
            }
        }
        const selectedOptionKeys = [];
        for (const value of values) {
            const key = valueToKey.get(value);
            if (typeof key === 'number' && !selectedOptionKeys.includes(key)) {
                selectedOptionKeys.push(key);
            }
        }
        if (selectedOptionKeys.length > 0) {
            selectedOptionKeys.sort((a, b) => a - b);
            summaries.push({ questionKey, selectedOptionKeys });
        }
    }
    return summaries;
};
/** A complete calendar date, the only value `ec__date`'s non-optional Date can store (SSP-4015). */
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
/**
 * The calendar day a revived `Date` was meant to carry, as ISO `YYYY-MM-DD`.
 *
 * A date-only answer reaches here as a `Date` only because something parsed it, and which half of
 * the instant is the intended day depends on how: `new Date('2026-03-04')` lands on UTC midnight,
 * where the UTC parts are the answer, while `new Date('2026-03-04T00:00:00')` lands on *local*
 * midnight, where `toISOString()` would report 3 March for anyone east of Greenwich and stamp a
 * day the signer never entered onto a permanent record. Midnight UTC is the one case where the
 * UTC parts are certainly right; anything else is read locally, which is right for both.
 *
 * Exported for tests.
 */
export const isoCalendarDay = (value) => {
    if (Number.isNaN(value.getTime())) {
        return '';
    }
    if (value.getUTCHours() === 0 && value.getUTCMinutes() === 0 && value.getUTCSeconds() === 0) {
        return value.toISOString().slice(0, 10);
    }
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${value.getFullYear()}-${month}-${day}`;
};
/**
 * Project the signed document's saved `ec__date` answers down to `{ fieldKey, value }` so the
 * bundle renders a date already on record in the field on load (SSP-4015) — the date counterpart
 * of {@link getSavedChoiceSelections}, and far simpler because there is no
 * `ec__selection_options` reverse mapping: the stored value *is* the answer.
 *
 * Cortex stores `ec__date.ec__value` as a `dateOnly` Date, which serializes as an ISO string. Only
 * the date half is forwarded, because that is what a native date input's `value` accepts — handing
 * it a full timestamp would leave the field blank.
 *
 * Exported for tests.
 */
export const getSavedDates = (documentDetail) => {
    const dateFieldKeys = new Set(getRuntimeData(documentDetail)
        .filter((def) => def?.ec__type === 'ec__date' && typeof def.ec__key === 'string' && def.ec__key)
        .map((def) => def.ec__key));
    if (dateFieldKeys.size === 0) {
        return [];
    }
    const saved = [];
    const entries = toArray(documentDetail?.ec__required_data);
    for (const entry of entries) {
        const fieldKey = entry?.ec__identifier;
        if (typeof fieldKey !== 'string' || !dateFieldKeys.has(fieldKey)) {
            continue;
        }
        const raw = Array.isArray(entry.ec__value) ? entry.ec__value[0] : entry.ec__value;
        // A `Date` instance as well as a string, because a client that revives dates hands back the
        // former and dropping it would cost the field its replay *and* the archived PDF its stamp.
        // The regex is a shape check, not a validity one: `2026-02-31` passes here and is refused
        // downstream by the native input and by the PDF baker.
        const isoDate = typeof raw === 'string' ? raw.slice(0, 10) : raw instanceof Date ? isoCalendarDay(raw) : '';
        if (ISO_DATE_PATTERN.test(isoDate)) {
            saved.push({ fieldKey, value: isoDate });
        }
        else {
            console.warn(`Dropping a stored date that is not a usable calendar date (${fieldKey}).`);
        }
    }
    return saved;
};
/**
 * Overlay answers known this session win over a server snapshot that has not caught up
 * (set_data is persisted without a refetch, then signing rebuilds the iframe from the
 * snapshot). `cleared` names fields the signer emptied this session, whose old value the
 * snapshot still carries until a refetch.
 */
export const mergeSavedFieldValues = (server, local, cleared = new Set()) => {
    const byKey = new Map();
    for (const item of server) {
        if (item.fieldKey && !cleared.has(item.fieldKey)) {
            byKey.set(item.fieldKey, item);
        }
    }
    for (const [fieldKey, value] of local) {
        if (value !== '') {
            byKey.set(fieldKey, { fieldKey, value });
        }
    }
    return [...byKey.values()];
};
/**
 * Project saved `ec__text` / `ec__numeric` / `ec__email` answers down to `{ fieldKey, value }`
 * so the bundle rehydrates the input on load — the free-text counterpart of {@link getSavedDates}.
 *
 * Cortex stores `ec__numeric.ec__value` as a Number; the overlay is a string input, so a number is
 * replayed as the canonical decimal string the host posts, the stamp prints, and the copy shows.
 * Empty answers are omitted.
 */
export const getSavedTexts = (documentDetail) => {
    const numericFieldKeys = new Set();
    const textFieldKeys = new Set(getRuntimeData(documentDetail)
        .filter((def) => typeof def.ec__key === 'string' &&
        def.ec__key &&
        // A thin PST expand can omit `ec__type`. Treat those keys as text so a stored
        // string/number still rehydrates; explicit date/choice types stay out.
        (!def.ec__type || isConsentTextInputType(def.ec__type)))
        .map((def) => {
        if (def.ec__type === 'ec__numeric') {
            numericFieldKeys.add(def.ec__key);
        }
        return def.ec__key;
    }));
    if (textFieldKeys.size === 0) {
        return [];
    }
    const saved = [];
    const entries = toArray(documentDetail?.ec__required_data);
    for (const entry of entries) {
        const fieldKey = entry?.ec__identifier;
        if (typeof fieldKey !== 'string' || !textFieldKeys.has(fieldKey)) {
            continue;
        }
        const raw = Array.isArray(entry.ec__value) ? entry.ec__value[0] : entry.ec__value;
        let value = '';
        if (typeof raw === 'number' && Number.isFinite(raw)) {
            value = String(raw);
        }
        else if (typeof raw === 'string') {
            value = numericFieldKeys.has(fieldKey) ? normalizeOverlayText('ec__numeric', raw) : raw;
        }
        if (value !== '') {
            saved.push({ fieldKey, value });
        }
    }
    return saved;
};
const getBundleFileExtension = (fileName) => fileName.split('.').pop()?.toLowerCase();
const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const normalizeRole = (role) => role?.trim().toLowerCase();
const getDisplayTitle = (title) => title.split(/[\\/]/).filter(Boolean).pop() || title;
const getMimeType = (extension) => {
    switch (extension) {
        case 'png':
            return 'image/png';
        case 'jpg':
        case 'jpeg':
            return 'image/jpeg';
        case 'gif':
            return 'image/gif';
        case 'webp':
            return 'image/webp';
        case 'svg':
            return 'image/svg+xml';
        case 'html':
            return 'text/html';
        case 'css':
            return 'text/css';
        case 'js':
            return 'text/javascript';
        case 'json':
            return 'application/json';
        case 'pdf':
            return 'application/pdf';
        default:
            return 'text/plain';
    }
};
const replaceBundleReferences = (source, urlByFileName) => Object.keys(urlByFileName)
    .sort((a, b) => b.length - a.length)
    .reduce((result, fileName) => {
    return [`./${fileName}`, `/${fileName}`, fileName].reduce((updatedResult, reference) => updatedResult.replace(new RegExp(escapeRegExp(reference), 'g'), urlByFileName[fileName]), result);
}, source);
const roleMatches = (a, b) => !!a && !!b && normalizeRole(a) === normalizeRole(b);
const findSignatureByKey = (documentDetail, signatureKey) => getRuntimeSignatures(documentDetail).find((signature) => signature.ec__key === signatureKey);
const bytesToBase64 = (bytes) => {
    let binary = '';
    for (let index = 0; index < bytes.length; index += 1) {
        binary += String.fromCharCode(bytes[index]);
    }
    return btoa(binary);
};
const blobToDataUrl = async (blob) => {
    const bytes = new Uint8Array(await blob.arrayBuffer());
    return `data:${blob.type || 'application/octet-stream'};base64,${bytesToBase64(bytes)}`;
};
const stringToDataUrl = (source, type) => blobToDataUrl(new Blob([source], { type }));
const isEconsentBundleHostMessage = (value) => {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const message = value;
    if (message[ECONSENT_BUNDLE_HOST_MESSAGE_TAG] !== true) {
        return false;
    }
    if (message.kind === 'cancel' || message.kind === 'track' || message.kind === 'sign' || message.kind === 'set-data') {
        return true;
    }
    if (message.kind !== 'request-name') {
        return false;
    }
    const request = message.request;
    return typeof message.requestId === 'string' && !!message.requestId && typeof request?.ec__key === 'string';
};
const buildEconsentBundleHighlightMessage = (keys) => ({
    [ECONSENT_BUNDLE_HOST_RESPONSE_TAG]: true,
    kind: 'highlight-outstanding',
    keys,
});
/** Disables (or re-enables) every overlay control while the signer's completion is being committed. */
const buildEconsentBundleLockMessage = (locked) => ({
    [ECONSENT_BUNDLE_HOST_RESPONSE_TAG]: true,
    kind: 'lock-overlays',
    locked,
});
const buildEconsentBundleNameResponse = (requestId, name) => ({
    [ECONSENT_BUNDLE_HOST_RESPONSE_TAG]: true,
    kind: 'name-response',
    requestId,
    name,
});
/**
 * Exported for tests: this string is the entire host↔bundle contract, and it is only ever executed
 * inside a blob-URL iframe, which jsdom cannot reach into.
 */
export const getHostHooksCode = (allowedSignerRole, signedSignatures, savedChoices, answerMetadata, savedDates, savedTexts) => {
    const signedSignatureSeed = signedSignatures
        ? `window.__econsent_signed_signatures__ = ${JSON.stringify(signedSignatures).replace(/</g, '\\u003c')};`
        : '';
    // Choice gating (SSP-4107): expose the current site user's signer *type*
    // so the bundle keeps only *their* role's radio/checkbox overlays
    // interactive and locks everyone else's (e.g. the participant's) answers.
    // `getMetadata()` did not previously exist on this host script, so this
    // is also the first time `isSignatureFieldInteractive` in app.tsx sees a
    // defined `signerType` here — non-matching-role signature overlays now
    // render as visually non-interactive too (the "site-app host parity"
    // gating SSP-3955 added to the bundle, never wired up on this host until
    // now). This does NOT change signature *authorization* — clicking one
    // was already independently blocked by the `activeSignerRole` checks
    // elsewhere in this file (e.g. the requestSignerName handler) regardless
    // of the bundle's own CSS interactive/disabled styling.
    // `signerType` is emitted ONLY when there's an active signer; without one
    // we fall back to `readOnly` so choices are display-only. See
    // `isChoiceFieldInteractive`.
    //
    // The signer's saved selections ride along here too, so the bundle pre-checks
    // answers it already has on load. `getMetadata()` is the client API's channel
    // for anything known only at render time, which is what this is — the
    // alternative, another `window.__econsent_*__` global, is not the sanctioned
    // mechanism.
    const metadataEntries = [
        answerMetadata?.signerType ? `signerType: ${JSON.stringify(answerMetadata.signerType)}` : '',
        answerMetadata ? `readOnly: ${answerMetadata.readOnly}` : '',
        savedChoices ? `savedChoices: ${JSON.stringify(savedChoices).replace(/</g, '\\u003c')}` : '',
        // Dates already entered ride along on the same channel, for the same reason (SSP-4015).
        // Omitted when empty, where `savedChoices` above emits `[]`: the bundle reads `savedDates ??
        // []`, so the two are equivalent to it, and every document that has no date fields — which is
        // every document published before this change — is then byte-for-byte the script it had before.
        savedDates?.length ? `savedDates: ${JSON.stringify(savedDates).replace(/</g, '\\u003c')}` : '',
        savedTexts?.length ? `savedTexts: ${JSON.stringify(savedTexts).replace(/</g, '\\u003c')}` : '',
    ].filter(Boolean);
    const metadataHook = metadataEntries.length
        ? `window.getMetadata = function () { return { ${metadataEntries.join(', ')} }; };`
        : '';
    return `
    (function () {
      ${signedSignatureSeed}
      ${metadataHook}

      // Bridge choice toggles and entered dates back to the host so an active
      // signer's answers persist via /routes/econsent/set_data (SSP-4107,
      // SSP-4015). The host re-checks role ownership before writing (least
      // privilege) and routes on the payload's field type.
      window.setDocumentData = function (payload) {
        post({ kind: 'set-data', payload: payload });
      };

      var pendingNameRequests = Object.create(null);
      var nextRequestSeq = 0;
      var allowedSignerRole = ${JSON.stringify(normalizeRole(allowedSignerRole) || null)};
      var signerComplete = ${JSON.stringify(!!answerMetadata?.signerComplete)};
      var turnLocked = ${JSON.stringify(!!answerMetadata?.turnLocked)};

      function post(message) {
        try {
          window.parent.postMessage(
            Object.assign({ ${JSON.stringify(ECONSENT_BUNDLE_HOST_MESSAGE_TAG)}: true }, message || {}),
            '*'
          );
        } catch (error) {
          // Ignore bridge failures so the bundle renderer keeps running.
        }
      }

      function overlayMatchesAllowedRole(overlay) {
        if (!allowedSignerRole) {
          return true;
        }
        var role = (overlay.getAttribute('data-signer-role') || '').toLowerCase();
        if (role && role === allowedSignerRole) {
          return true;
        }
        var label = (overlay.getAttribute('aria-label') || overlay.textContent || '').toLowerCase();
        return (
          label.indexOf('role: ' + allowedSignerRole + '.') !== -1 ||
          label.indexOf('for ' + allowedSignerRole) !== -1
        );
      }

      // A signer whose part is complete has spent their own overlays too, optional ones included:
      // the host denies those requests and the backend rejects them as cannotUpdateCompletedSigner,
      // so they are styled the way every other unavailable overlay already is rather than being
      // left looking clickable (SSP-4433). Signatures they captured keep their handler — clicking
      // one opens the read-only "already captured" view.
      function overlayIsUnavailable(overlay) {
        if (!overlayMatchesAllowedRole(overlay)) {
          return true;
        }
        if (overlay.classList.contains('consent-signature-overlay--signed')) {
          return false;
        }

        return turnLocked || signerComplete;
      }

      function disableUnavailableOverlays() {
        if (!allowedSignerRole && !signerComplete) {
          return;
        }
        var overlays = document.querySelectorAll('.consent-signature-overlay');
        overlays.forEach(function (overlay) {
          if (!overlayIsUnavailable(overlay)) {
            return;
          }
          overlay.classList.add('consent-signature-overlay--disabled');
          overlay.style.pointerEvents = 'none';
          overlay.style.cursor = 'not-allowed';
          overlay.removeAttribute('role');
          overlay.setAttribute('aria-disabled', 'true');
        });
      }

      window.requestSignerName = function (request) {
        return new Promise(function (resolve) {
          var requestId = 'lumos_' + (++nextRequestSeq) + '_' + Date.now();
          pendingNameRequests[requestId] = resolve;
          post({ kind: 'request-name', requestId: requestId, request: request });
        });
      };

      function highlightOutstanding(keys) {
        keys = keys || [];
        var i;
        var el;
        var unsigned;
        var key;
        for (i = 0; i < keys.length; i++) {
          key = String(keys[i]).replace(/"/g, '');
          try {
            el = document.querySelector('[data-ec-key="' + key + '"], [name="' + key + '"]');
          } catch (error) {
            el = null;
          }
          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ block: 'center', inline: 'nearest' });
            if (typeof el.focus === 'function') {
              el.focus();
            }
            return;
          }
        }
        unsigned = document.querySelector('.consent-signature-overlay:not(.consent-signature-overlay--signed):not(.consent-signature-overlay--disabled)');
        if (unsigned && typeof unsigned.scrollIntoView === 'function') {
          unsigned.scrollIntoView({ block: 'center', inline: 'nearest' });
        }
      }

      // Completion is about to lock the record: anything typed from here on could not be saved, so
      // the controls stop taking input. Only controls locked here are re-enabled if completion fails.
      function setOverlaysLocked(locked) {
        if (document.documentElement && document.documentElement.setAttribute) {
          document.documentElement.setAttribute('aria-busy', locked ? 'true' : 'false');
        }
        if (!document.querySelectorAll) {
          return;
        }
        var fields = document.querySelectorAll('textarea, input');
        var i;
        for (i = 0; i < fields.length; i++) {
          var field = fields[i];
          if (locked && !field.disabled) {
            field.disabled = true;
            field.setAttribute('data-econsent-host-locked', '1');
          } else if (!locked && field.getAttribute('data-econsent-host-locked') === '1') {
            field.disabled = false;
            field.removeAttribute('data-econsent-host-locked');
          }
        }
      }

      window.addEventListener('message', function (event) {
        var data = event.data || {};
        if (data[${JSON.stringify(ECONSENT_BUNDLE_HOST_RESPONSE_TAG)}] !== true) {
          return;
        }
        if (data.kind === 'highlight-outstanding') {
          highlightOutstanding(data.keys);
          return;
        }
        if (data.kind === 'lock-overlays') {
          setOverlaysLocked(!!data.locked);
          return;
        }
        if (data.kind !== 'name-response') {
          return;
        }
        var pending = pendingNameRequests[data.requestId];
        if (typeof pending !== 'function') {
          return;
        }
        delete pendingNameRequests[data.requestId];
        pending(typeof data.name === 'string' ? data.name : null);
      });

      window.sign = function (payload) {
        post({ kind: 'sign', signature: payload && payload.signature, name: payload && payload.name });
      };

      window.track = function (event) {
        post({ kind: 'track', event: event });
      };

      window.cancel = function () {
        post({ kind: 'cancel' });
      };

      function applySignedLabelNowrap() {
        if (document.head && !document.getElementById('econsent-signed-label-css')) {
          var sheet = document.createElement('style');
          sheet.id = 'econsent-signed-label-css';
          sheet.textContent =
            '.consent-signature-overlay--signed .consent-signature-overlay-label{white-space:nowrap!important;overflow:visible!important;}';
          document.head.appendChild(sheet);
        }
        if (!document.querySelectorAll) {
          return;
        }
        // Only a signed stamp: the hidden unsigned prompt must not widen its overlay.
        var labels = document.querySelectorAll('.consent-signature-overlay--signed .consent-signature-overlay-label');
        var i;
        for (i = 0; i < labels.length; i++) {
          var el = labels[i];
          if (!el.style || typeof el.style.setProperty !== 'function') {
            continue;
          }
          el.style.setProperty('white-space', 'nowrap', 'important');
          el.style.setProperty('overflow', 'visible', 'important');
          var overlay = el.parentElement;
          if (overlay && overlay.clientWidth && el.scrollWidth > overlay.clientWidth) {
            overlay.style.width = overlay.clientWidth + (el.scrollWidth - overlay.clientWidth) + 'px';
          }
        }
      }

      function startOverlayObserver() {
        disableUnavailableOverlays();
        applySignedLabelNowrap();
        [0, 50, 200, 800].forEach(function (ms) {
          setTimeout(applySignedLabelNowrap, ms);
        });
        if (document.body) {
          new MutationObserver(function () {
            disableUnavailableOverlays();
            applySignedLabelNowrap();
          }).observe(document.body, { childList: true, subtree: true });
        }
      }

      if (document.body) {
        startOverlayObserver();
      } else {
        document.addEventListener('DOMContentLoaded', startOverlayObserver, { once: true });
      }
    })();
`;
};
const injectHostHooksScript = (html, allowedSignerRole, signedSignatures, savedChoices, answerMetadata, savedDates, savedTexts) => {
    const scriptTag = `<script type="text/javascript">${getHostHooksCode(allowedSignerRole, signedSignatures, savedChoices, answerMetadata, savedDates, savedTexts)}</script>`;
    if (html.includes('</head>')) {
        return html.replace('</head>', `${scriptTag}</head>`);
    }
    return `<head>${scriptTag}</head>${html}`;
};
const createBundlePreview = async (bundleUrl, allowedSignerRole, signedSignatures, savedChoices, answerMetadata, savedDates, savedTexts) => {
    const response = await fetch(bundleUrl);
    if (!response.ok) {
        throw new Error('Unable to fetch consent bundle.');
    }
    const zip = await JSZip.loadAsync(await response.blob());
    const files = await Promise.all(Object.keys(zip.files).map(async (fileName) => {
        const zipEntry = zip.files[fileName];
        if (zipEntry.dir) {
            return null;
        }
        const extension = getBundleFileExtension(fileName);
        const isText = TEXT_EXTENSIONS.includes(extension || '');
        return {
            name: fileName,
            data: await zipEntry.async(isText ? 'string' : 'blob'),
            extension,
        };
    }));
    const bundleFiles = files.filter((file) => !!file);
    const html = bundleFiles.find((file) => file.name === 'index.html') ||
        bundleFiles.find((file) => file.name.endsWith('/index.html'));
    if (!html || typeof html.data !== 'string') {
        throw new Error('No index.html was found in the consent bundle.');
    }
    const urlByFileName = {};
    await Promise.all(bundleFiles
        .filter((file) => file.name !== html.name)
        .filter((file) => !TEXT_EXTENSIONS.includes(file.extension || ''))
        .map(async (file) => {
        urlByFileName[file.name] = await blobToDataUrl(file.data);
    }));
    await Promise.all(bundleFiles
        .filter((file) => file.name !== html.name)
        .filter((file) => TEXT_EXTENSIONS.includes(file.extension || ''))
        .map(async (file) => {
        const source = replaceBundleReferences(file.data, urlByFileName);
        urlByFileName[file.name] = await stringToDataUrl(source, getMimeType(file.extension));
    }));
    const htmlWithHooks = injectHostHooksScript(html.data, allowedSignerRole, signedSignatures, savedChoices, answerMetadata, savedDates, savedTexts);
    const previewHtml = replaceBundleReferences(htmlWithHooks, urlByFileName);
    return {
        previewHtml,
    };
};
export const DocumentModal = ({ document, isOpen, onClose, accountId, accountEmail, accountDisplayName, apiClient, getApiErrorMessage, isDocGenEconsentEnabled, participantId, canVoidDocument, onVoidDocument, classicDocumentUrl, canDownloadConsentPdf = false, isNoPiiSite = false, }) => {
    const translate = useLocalizeMessage();
    // The baker must format dates in the document's locale, not the browser's.
    const appLocale = useContext(LocaleContext);
    // Resolved once and shared: `useLocalizeMessage` returns a fresh function
    // identity on every render, so anything memoised must depend on the resolved
    // string rather than on the translator. Undefined when the locale has no
    // entry, which makes the manifestation fall back to the stored token instead
    // of printing the lookup key. Signing still proceeds: an empty pack must not
    // disable Sign.
    const localizedSignatureReasonRaw = translate(SIGNATURE_REASON_KEY);
    const localizedSignatureReason = localizedSignatureReasonRaw && localizedSignatureReasonRaw !== SIGNATURE_REASON_KEY
        ? localizedSignatureReasonRaw
        : undefined;
    // The role and action key sets are fixed and small, so they resolve once per
    // locale. Depending on `translate` itself would defeat every memo below - it
    // returns a fresh identity on each render - and the locale is what actually
    // changes the strings.
    const signatureLabels = useMemo(() => ({
        roles: Object.fromEntries(Object.entries(SIGNER_ROLE_LABEL_KEYS).map(([role, key]) => [role, resolveKey(translate, key)])),
        actions: Object.fromEntries(Object.entries(SIGNATURE_ACTION_KEYS).map(([method, key]) => [method, resolveKey(translate, key)])),
    }), [appLocale]);
    const { addNotification } = useToastNotification();
    const queryClient = useQueryClient();
    const iframeRef = useRef(null);
    const [previewHtml, setPreviewHtml] = useState();
    const [previewError, setPreviewError] = useState();
    const [password, setPassword] = useState('');
    const [printedName, setPrintedName] = useState(accountDisplayName);
    const defaultInitials = useMemo(() => getDisplayNameInitials(accountDisplayName), [accountDisplayName]);
    const [initials, setInitials] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCompleting, setIsCompleting] = useState(false);
    const [isCopyingPdf, setIsCopyingPdf] = useState(false);
    const [signatureRequest, setSignatureRequest] = useState(null);
    const [signatureModalMode, setSignatureModalMode] = useState('sign');
    // Choice `ec__identifier`s the active signer has answered this session via
    // set_data. We track them locally (instead of refetching, which rebuilds
    // the iframe) so the completion gate counts them as satisfied and the
    // signer can be marked complete (SSP-4107).
    const [locallySavedDataIds, setLocallySavedDataIds] = useState(() => new Set());
    // Typed overlay values for the same session. Keys-only `locallySavedDataIds` is enough
    // for the completion gate; the preview rebuild after sign needs the strings themselves
    // because set_data does not refetch `ec__required_data`.
    const locallySavedTextsRef = useRef(new Map());
    const locallySavedDatesRef = useRef(new Map());
    // Text fields the signer cleared this session. The server snapshot still lists their old value
    // until a refetch, so both the completion gate and the preview rebuild must subtract them.
    const [locallyClearedDataIds, setLocallyClearedDataIds] = useState(() => new Set());
    const locallyClearedDataIdsRef = useRef(locallyClearedDataIds);
    locallyClearedDataIdsRef.current = locallyClearedDataIds;
    // Text fields whose on-screen value Cortex would refuse. The record no longer holds an answer for
    // them, and signing and completion wait until the signer fixes or clears each one.
    const invalidTextIdsRef = useRef(new Set());
    const lateEditNoticeShownRef = useRef(false);
    const showLateEditNotice = () => {
        if (lateEditNoticeShownRef.current) {
            return;
        }
        lateEditNoticeShownRef.current = true;
        addNotification({
            title: '',
            subtitle: translate('info.participant_documents.document_completed'),
            type: 'info',
        });
    };
    // Answers that arrived after set_signer_complete was sent, latest per field.
    const heldCommitEditsRef = useRef(new Map());
    const handleHostMessageRef = useRef(() => undefined);
    const leaveRef = useRef(() => undefined);
    // Keys whose set_data has already succeeded this session. A later keystroke
    // that 500s (Cortex busy, or cannotUpdateCompletedSigner after auto-complete)
    // must not drop them from the completion gate — reload would show the same
    // answers because the earlier write is already on the server.
    const confirmedDataIdsRef = useRef(new Set());
    // In-flight set_data requests, awaited before set_signer_complete so the
    // server has the answers persisted before it validates completion.
    const pendingSetDataRef = useRef([]);
    // A signer correcting an answer writes the same key twice in quick succession. Per key, the
    // writes are chained so the server cannot apply them out of order, and each write remembers its
    // own position so a failed one only reverts the UI if nothing newer has replaced it — otherwise
    // an early failure would erase an answer the signer has since successfully saved, leaving the
    // completion gate blocking a document the server considers answered.
    const dataWriteChainRef = useRef(new Map());
    const dataWriteSeqRef = useRef(new Map());
    // Writes waiting to be sent: text waiting out the debounce, or a choice/date held back by an
    // invalid text field. Only an answered required one may trigger completion; a clear still has
    // to reach Cortex but never completes anything.
    const textPersistFlushRef = useRef(new Map());
    // What Cortex last accepted for a text or date field this session, restored when a later write fails.
    const confirmedValuesRef = useRef(new Map());
    // Keys whose latest write Cortex refused. The overlay still shows the refused value, so signing and
    // completion wait until the signer enters it again and it saves.
    const unsavedDataIdsRef = useRef(new Set());
    // Once the signer's last required item is saved this session completes them, and Cortex refuses
    // every write after that. A pause in typing is not the signer finishing, so a queued text write
    // that would complete them waits for Done (or sign, or leaving) instead of the debounce: otherwise
    // a pause after "Pat" records "Pat" and refuses the "Pat Avery" the screen goes on to show.
    const [textCommitHeld, setTextCommitHeld] = useState(false);
    const textFlushWouldCompleteRef = useRef(() => false);
    /** Coalesce overlay keystrokes: one set_data after typing pauses, then auto-complete. */
    const TEXT_PERSIST_DEBOUNCE_MS = 500;
    const flushPendingTextWrites = () => {
        setTextCommitHeld(false);
        const writes = [...textPersistFlushRef.current.entries()];
        textPersistFlushRef.current.clear();
        let lastKey;
        let lastPersist;
        const persists = [];
        for (const [questionKey, { write, answered, required }] of writes) {
            const persist = write();
            persists.push(persist);
            if (answered && required) {
                lastKey = questionKey;
                lastPersist = persist;
            }
        }
        return { lastKey, lastPersist, persists };
    };
    const signaturePromptOpenRef = useRef(false);
    const persistDebouncedTexts = useDebouncedCallback(() => {
        if (signaturePromptOpenRef.current || isCompleteRef.current) {
            return;
        }
        if (textFlushWouldCompleteRef.current()) {
            setTextCommitHeld(true);
            return;
        }
        const { lastKey, lastPersist } = flushPendingTextWrites();
        if (lastKey) {
            void completeSignerIfReadyRef.current({ completedDataKey: lastKey, persist: lastPersist });
        }
    }, TEXT_PERSIST_DEBOUNCE_MS);
    useEffect(() => {
        if (!signatureRequest) {
            signaturePromptOpenRef.current = false;
        }
    }, [signatureRequest]);
    /** Sends and awaits every queued write. False when the overlay shows a value Cortex refused. */
    const drainPendingSetDataWrites = async () => {
        for (;;) {
            persistDebouncedTexts.cancel();
            flushPendingTextWrites();
            const chains = [...dataWriteChainRef.current.values()];
            const inFlight = pendingSetDataRef.current;
            pendingSetDataRef.current = [];
            if (chains.length === 0 && inFlight.length === 0) {
                break;
            }
            await Promise.allSettled([...chains, ...inFlight]);
            // Typing that arrived while those settled is still waiting out the debounce; go round again.
            if (pendingSetDataRef.current.length === 0 && textPersistFlushRef.current.size === 0) {
                break;
            }
        }
        return unsavedDataIdsRef.current.size === 0;
    };
    // Unmount, a route change, or closing the tab must not drop what is still waiting out the debounce
    // (use-debounce skips its call once unmounted). Save it, but never complete or archive from here:
    // the page may be gone before either could finish.
    const flushOnLeaveRef = useRef(() => { });
    flushOnLeaveRef.current = () => {
        persistDebouncedTexts.cancel();
        flushPendingTextWrites();
    };
    useEffect(() => {
        const flush = () => flushOnLeaveRef.current();
        window.addEventListener('pagehide', flush);
        return () => {
            window.removeEventListener('pagehide', flush);
            flush();
        };
    }, []);
    // Latest "does the active signer still have unanswered required choices?"
    // flag, mirrored into a ref so the (rarely re-subscribed) message listener
    // can gate signing on it without a stale closure (SSP-4107).
    const hasPendingRequiredDataRef = useRef(false);
    // Same pattern for navigate-away: Cancel/Close stays visible so the modal
    // never renders with zero buttons, but the click is intercepted while
    // required elements remain (toast + highlight instead of leave).
    const hideNavigateAwayRef = useRef(false);
    const warnOutstandingAndHighlightRef = useRef(() => undefined);
    const warnInvalidTextAndHighlightRef = useRef(() => undefined);
    const outstandingRequiredRef = useRef({ keys: [], labels: [] });
    const completeSignerIfReadyRef = useRef(async () => undefined);
    const isCompleteRef = useRef(false);
    const completeSignerInFlightRef = useRef(false);
    const archiveInFlightRef = useRef(false);
    // True from the moment set_signer_complete is sent: answers after that cannot be saved.
    const completionCommittingRef = useRef(false);
    // Set when a completion attempt fails. Its PUT may still have landed (a lost response), so a later
    // write that Cortex refuses as a completed signer means the record is complete. `archive` is false
    // when that attempt came from signing, whose own flow archives the record.
    const releasedCompletionRef = useRef(null);
    const latchServerCompletionRef = useRef(async () => undefined);
    // Session latch render cannot overwrite: the choice path never refetches, so
    // currentSigner/document status stay un-complete after set_signer_complete.
    const [completedThisSession, setCompletedThisSession] = useState(false);
    const { uploadSignedPdf } = useSignedPdfUpload({
        apiClient,
        accountId,
    });
    const generateAndUploadSignedPdf = useCallback(async (docDetail) => {
        if (docDetail.ec__status !== 'complete') {
            return;
        }
        const docBundleUrl = docDetail.ec__published_studio_template?.ec__bundle?.url;
        if (!docBundleUrl) {
            return;
        }
        try {
            // A document whose only answered fields are dates still has a record to archive, and the
            // Download and Print copies already stamp one — the two surfaces have to agree, or the
            // participant's copy shows a date that the stored archive never received.
            const summaries = getSignedSignatureSummaries(docDetail, localizedSignatureReason, signatureLabels);
            const savedDates = mergeSavedFieldValues(getSavedDates(docDetail), locallySavedDatesRef.current);
            const savedTexts = mergeSavedFieldValues(getSavedTexts(docDetail), locallySavedTextsRef.current, locallyClearedDataIdsRef.current);
            if (!summaries.length && savedDates.length === 0 && savedTexts.length === 0) {
                return;
            }
            const { pdfBytes, signedOverlays, dateOverlays, textOverlays } = await extractBundlePdfData(docBundleUrl, summaries, savedDates, savedTexts);
            if (!signedOverlays.length && dateOverlays.length === 0 && textOverlays.length === 0) {
                return;
            }
            await uploadSignedPdf({
                pdfBytes,
                signatures: signedOverlays,
                dates: dateOverlays,
                texts: textOverlays,
                signedDocId: docDetail._id,
                fileName: `${docDetail._id}_signed_consent.pdf`,
                locale: appLocale,
            });
        }
        catch (error) {
            console.error('Failed to generate/upload signed PDF:', error);
            addNotification({
                title: '',
                subtitle: translate('error.participant_documents.download_failed'),
                type: 'error',
            });
        }
    }, [addNotification, appLocale, localizedSignatureReason, signatureLabels, translate, uploadSignedPdf]);
    const buildCopyPdfBytes = useCallback(async (docDetail) => {
        const summaries = getSignedSignatureSummaries(docDetail, localizedSignatureReason, signatureLabels);
        return buildConsentCopyPdfBytes(docDetail, summaries, appLocale, mergeSavedFieldValues(getSavedDates(docDetail), locallySavedDatesRef.current), isNoPiiSite
            ? []
            : mergeSavedFieldValues(getSavedTexts(docDetail), locallySavedTextsRef.current, locallyClearedDataIdsRef.current), getSavedChoiceSelections(docDetail));
    }, [localizedSignatureReason, signatureLabels, appLocale, isNoPiiSite]);
    const closeSignatureModal = () => {
        respondToSignatureRequest(null);
        setSignatureRequest(null);
    };
    const fetchDocumentDetail = async () => {
        if (!document?._id) {
            throw new Error('Document is unavailable.');
        }
        const response = await apiClient.get(`/v2/account/${accountId}/ec__site_documents/${document._id}`, {
            params: {
                paths: DOCUMENT_DETAIL_PATHS,
                expand: ['ec__published_studio_template'],
                passive: true,
            },
        });
        const payload = response.data;
        return (payload && typeof payload === 'object' && 'data' in payload ? payload.data : payload);
    };
    // Wall-clock deadline for FRS 14.2, held in state so crossing it actually re-renders the
    // modal into the failure branch. A ref would not. Passing `false` (not a function that
    // returns false) is what makes TanStack Query drop an already-scheduled poll timer.
    const [hasBundleWaitElapsed, setHasBundleWaitElapsed] = useState(false);
    const { data: documentDetail, isFetching: isDocumentLoading, isError: isDocumentError, refetch: refetchDocumentDetail, } = useQuery({
        queryKey: ['participantDocumentDetail', document?._id],
        queryFn: fetchDocumentDetail,
        enabled: isOpen && !!document?._id,
        refetchOnWindowFocus: false,
        // Cortex finalizes a freshly-published bundle asynchronously — ec__bundle.url (and the file
        // it points to) can lag several minutes behind the resync job that created this document, so a
        // one-shot fetch can permanently see no url. Poll until it shows up, and stop on the same
        // deadline that surfaces the failure, so what is polled and what the user is told can never
        // disagree — an indefinite poll in a participant-facing modal would spin forever with no
        // feedback if the bundle genuinely never finishes processing.
        refetchInterval: (query) => participantDocumentRefetchInterval(query.state.data, hasBundleWaitElapsed),
    });
    const publishedStudioTemplate = documentDetail?.ec__published_studio_template;
    const bundleUrl = publishedStudioTemplate?.ec__bundle?.url;
    // A classic (non-Studio) eConsent template is never published to a Studio template, so no bundle
    // is ever coming — permanent and by design, not the transient finalization lag the preparing
    // state and its deadline exist for. Waiting one out would end in "preparation failed, try again"
    // about a document that had nothing to prepare.
    const hasNoBundleSource = !isDocumentError && !!documentDetail && !publishedStudioTemplate;
    const isBundleProcessing = !isDocumentError && !!documentDetail && !!publishedStudioTemplate && !bundleUrl;
    // Keyed on the *condition*, deliberately not on the polled payload. A stalled bundle returns an
    // identical payload on every poll and TanStack Query's structural sharing then hands back the
    // previous object, so an effect depending on `documentDetail` never re-runs in the one case this
    // timeout exists for. Elapsed time is the only signal that still moves.
    useEffect(() => {
        if (!isOpen || !isBundleProcessing) {
            setHasBundleWaitElapsed(false);
            return;
        }
        const timer = setTimeout(() => setHasBundleWaitElapsed(true), BUNDLE_NOT_READY_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [isOpen, isBundleProcessing]);
    const isBundleProcessingTimedOut = isBundleProcessing && hasBundleWaitElapsed;
    const copyConsentPdf = useCallback(async (mode) => {
        if (!documentDetail || isCopyingPdf) {
            return;
        }
        setIsCopyingPdf(true);
        try {
            const pdfBytes = await buildCopyPdfBytes(documentDetail);
            if (!pdfBytes) {
                addNotification({
                    title: '',
                    subtitle: translate('error.participant_documents.download_failed'),
                    type: 'error',
                });
                return;
            }
            const blob = new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' });
            const fileName = `${documentDetail.ec__identifier || documentDetail._id}_consent.pdf`;
            if (mode === 'download') {
                downloadBlob(blob, fileName);
            }
            else {
                printPdfBlob(blob);
            }
        }
        catch (error) {
            console.error('Failed to copy consent PDF:', error);
            addNotification({
                title: '',
                subtitle: translate('error.participant_documents.download_failed'),
                type: 'error',
            });
        }
        finally {
            setIsCopyingPdf(false);
        }
    }, [addNotification, buildCopyPdfBytes, documentDetail, isCopyingPdf, translate]);
    const currentSigner = useMemo(() => {
        return documentDetail?.ec__accepted_signers?.find((signer) => getSignerAccountId(signer) === accountId);
    }, [accountId, documentDetail]);
    const activeSignerRole = currentSigner?.ec__signer_role;
    const signatureInvites = toArray(documentDetail?.ec__signature_invites);
    const isDelayedLocked = isDelayedSigningLocked(documentDetail);
    const inviteWave = activeInviteWave(signatureInvites);
    // Cortex adds the accepted-signer row only once a signer's wave opens, so a later signer is
    // recognised from their own invite until then.
    const ownInvite = activeSignerRole ? undefined : findOwnInvite(signatureInvites, accountEmail);
    const isOutOfTurn = activeSignerRole
        ? !isRoleInActiveWave(activeSignerRole, signatureInvites)
        : isInviteOutOfTurn(ownInvite, signatureInvites);
    const turnLocked = isDelayedLocked || isOutOfTurn;
    const blockingRole = inviteWave[0]?.ec__signer_role;
    const signingBlockedMessage = isDelayedLocked
        ? translate('error.participant_documents.delayed_signing_locked')
        : translate('error.participant_documents.signer_order_not_reached', {
            role: blockingRole || translate('error.participant_documents.earlier_signer'),
        });
    const ownInviteRole = ownInvite?.ec__signer_role;
    const previewSignerRole = useMemo(() => {
        if (activeSignerRole) {
            return activeSignerRole;
        }
        if (ownInviteRole) {
            return ownInviteRole;
        }
        return getRuntimeSignerRoles(documentDetail).find((role) => role.ec__signer_type === 'internal signer')?.ec__role;
    }, [activeSignerRole, documentDetail, ownInviteRole]);
    const signedSignatureIds = useMemo(() => getSignedSignatureIdentifiers(documentDetail), [documentDetail]);
    const signedSignatureSummaries = useMemo(() => getSignedSignatureSummaries(documentDetail, localizedSignatureReason, signatureLabels), [documentDetail, localizedSignatureReason, signatureLabels]);
    const savedChoiceSelections = useMemo(() => getSavedChoiceSelections(documentDetail), [documentDetail]);
    const savedDateValues = useMemo(() => getSavedDates(documentDetail), [documentDetail]);
    const savedTextValues = useMemo(() => getSavedTexts(documentDetail), [documentDetail]);
    const requestedDataRoleByKey = useMemo(() => getRequestedDataRoleByKey(documentDetail), [documentDetail]);
    // The active signer's choices stay editable (gated by their signer type)
    // only while their signing is still pending. Once that signer — or the
    // whole document — is complete, or when there's no active signer at all,
    // choices become display-only. Delayed-signing lock and out-of-turn sit
    // in the same read-only bucket so overlays never invite a credential prompt.
    const answerMetadata = useMemo(() => {
        const signerType = getSignerTypeForRole(documentDetail, activeSignerRole);
        // The choice path completes without a refetch, so the server status lags; lock on our own completion too.
        const signerComplete = completedThisSession || currentSigner?.ec__status === 'complete' || documentDetail?.ec__status === 'complete';
        // `signerComplete` rides along unreduced because the overlays need the signer's own status,
        // which `readOnly` folds together with "there is no active signer at all" (SSP-4433).
        return {
            signerType,
            readOnly: !signerType || signerComplete || turnLocked,
            signerComplete,
            turnLocked,
        };
    }, [documentDetail, activeSignerRole, currentSigner, completedThisSession, turnLocked]);
    const previewFailedMessage = translate('error.participant_documents.preview_failed');
    useEffect(() => {
        let cancelled = false;
        // Signing / completion refetch this effect to paint stamps. Blanking srcDoc first
        // unmounts the iframe and drops in-memory overlay values the server snapshot may
        // not yet include. Leave the current preview up until the rebuilt HTML is ready.
        setPreviewError(undefined);
        if (!isOpen || !bundleUrl) {
            return;
        }
        createBundlePreview(bundleUrl, previewSignerRole, isDocGenEconsentEnabled ? signedSignatureSummaries : undefined, isDocGenEconsentEnabled ? savedChoiceSelections : undefined, isDocGenEconsentEnabled ? answerMetadata : undefined, isDocGenEconsentEnabled ? mergeSavedFieldValues(savedDateValues, locallySavedDatesRef.current) : undefined, isDocGenEconsentEnabled
            ? mergeSavedFieldValues(savedTextValues, locallySavedTextsRef.current, locallyClearedDataIdsRef.current)
            : undefined)
            .then((preview) => {
            if (cancelled) {
                return;
            }
            setPreviewHtml(preview.previewHtml);
        })
            .catch(() => {
            if (!cancelled) {
                setPreviewError(previewFailedMessage);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [
        bundleUrl,
        isDocGenEconsentEnabled,
        isOpen,
        previewFailedMessage,
        previewSignerRole,
        signedSignatureSummaries,
        savedChoiceSelections,
        savedDateValues,
        savedTextValues,
        answerMetadata,
    ]);
    useEffect(() => {
        if (!isOpen) {
            // Typing still waiting out the debounce is saved, not dropped, when the modal closes.
            persistDebouncedTexts.cancel();
            flushPendingTextWrites();
            setPassword('');
            setInitials('');
            setPreviewError(undefined);
            setPreviewHtml(undefined);
            setSignatureRequest(null);
            setLocallySavedDataIds(new Set());
            setLocallyClearedDataIds(new Set());
            lateEditNoticeShownRef.current = false;
            heldCommitEditsRef.current.clear();
            locallySavedTextsRef.current.clear();
            locallySavedDatesRef.current.clear();
            confirmedDataIdsRef.current.clear();
            pendingSetDataRef.current = [];
            dataWriteChainRef.current.clear();
            dataWriteSeqRef.current.clear();
            unsavedDataIdsRef.current.clear();
            invalidTextIdsRef.current.clear();
            setCompletedThisSession(false);
            setIsCompleting(false);
            completeSignerInFlightRef.current = false;
            archiveInFlightRef.current = false;
            completionCommittingRef.current = false;
            releasedCompletionRef.current = null;
        }
    }, [isOpen, persistDebouncedTexts]);
    useEffect(() => {
        const handleHostMessage = (data) => {
            if (data.kind === 'cancel') {
                if (!hideNavigateAwayRef.current) {
                    leaveRef.current();
                    return;
                }
                warnOutstandingAndHighlightRef.current();
                return;
            }
            if (data.kind === 'set-data') {
                const { payload } = data;
                const questionKey = payload?.questionKey;
                const ownerRole = typeof questionKey === 'string' ? requestedDataRoleByKey.get(questionKey) : undefined;
                // Least privilege: only persist answers assigned to the active
                // signer's role (the bundle already gates interactivity by signer
                // type, this is the server-side-safe backstop).
                if (documentDetail?._id &&
                    activeSignerRole &&
                    !turnLocked &&
                    typeof questionKey === 'string' &&
                    questionKey &&
                    (!ownerRole || roleMatches(ownerRole, activeSignerRole))) {
                    // Once set_signer_complete is on its way the record is being locked: a later answer could
                    // not be saved, so it must not be shown as committed either. Ignore it before touching
                    // local state. (Answers arriving while completion is still draining earlier writes are
                    // accepted and drained with them.) Completion can still fail, so the latest ignored edit
                    // per field is held: it is applied if the record unlocks, and only a completion that
                    // succeeded tells the signer the document is already complete.
                    if (isCompleteRef.current) {
                        showLateEditNotice();
                        return;
                    }
                    if (completionCommittingRef.current) {
                        heldCommitEditsRef.current.set(questionKey, data);
                        return;
                    }
                    // A date arrives as a single ISO string and a choice as an array of reference values, so
                    // the field type decides the `ec__value` shape Cortex is handed. The published type wins,
                    // so a mislabelled bundle payload cannot change that shape. Older templates omit it on
                    // overlays, so fall back to the payload — otherwise text is treated as a choice and each
                    // keystroke posts immediately instead of waiting for the debounce.
                    const datum = getRuntimeData(documentDetail).find((entry) => entry.ec__key === questionKey);
                    const typeFromPayload = typeof payload?.type === 'string' ? payload.type : undefined;
                    const type = datum?.ec__type ||
                        (isConsentTextInputType(typeFromPayload) || typeFromPayload === 'ec__date' ? typeFromPayload : undefined) ||
                        'ec__text_choice';
                    const isDate = type === 'ec__date';
                    const isTextInput = isConsentTextInputType(type);
                    const stringValue = typeof payload?.value === 'string' ? payload.value : '';
                    const values = Array.isArray(payload?.selectedValues) ? payload.selectedValues : [];
                    // Exactly what Cortex receives and validates, so nothing is posted that it would refuse.
                    const textValue = isTextInput ? normalizeOverlayText(type, stringValue) : '';
                    const textIssue = isTextInput ? overlayTextIssue(type, textValue) : undefined;
                    const answered = isDate ? ISO_DATE_PATTERN.test(stringValue) : isTextInput ? !textIssue : values.length > 0;
                    // `ec__date` stores a non-optional Date, so an empty or part-typed one would fail validation
                    // and cost the signer an error toast for a value they were still entering.
                    if (isDate && !answered) {
                        return;
                    }
                    // A text value Cortex would refuse (bad format, unprintable characters) must not leave the
                    // last accepted value on record looking like the answer: it is cleared like an emptied
                    // field, and signing and completion wait until the signer fixes it.
                    if (textIssue && textIssue !== 'empty') {
                        invalidTextIdsRef.current.add(questionKey);
                    }
                    else {
                        invalidTextIdsRef.current.delete(questionKey);
                    }
                    // A cleared text field is unanswered again, so a required one blocks completion. Cortex
                    // deletes the stored datum when handed '', which is only worth a request when a value
                    // was ever sent; a value still waiting out the debounce is simply dropped.
                    const clearsText = !!textIssue;
                    const everSent = clearsText &&
                        (dataWriteChainRef.current.has(questionKey) ||
                            getCompletedDataIdentifiers(documentDetail).has(questionKey));
                    // What the clear replaces, restored if Cortex refuses to delete it.
                    const textBeforeClear = clearsText ? locallySavedTextsRef.current.get(questionKey) : undefined;
                    const answeredBeforeClear = clearsText &&
                        (textBeforeClear !== undefined || getCompletedDataIdentifiers(documentDetail).has(questionKey));
                    const confirmedBeforeClear = clearsText && confirmedDataIdsRef.current.has(questionKey);
                    if (clearsText) {
                        locallySavedTextsRef.current.delete(questionKey);
                        confirmedDataIdsRef.current.delete(questionKey);
                        setLocallyClearedDataIds((previous) => previous.has(questionKey) ? previous : new Set(previous).add(questionKey));
                    }
                    else if (isTextInput) {
                        locallySavedTextsRef.current.set(questionKey, textValue);
                    }
                    else if (isDate) {
                        locallySavedDatesRef.current.set(questionKey, stringValue);
                    }
                    if (answered) {
                        setLocallyClearedDataIds((previous) => {
                            if (!previous.has(questionKey)) {
                                return previous;
                            }
                            const next = new Set(previous);
                            next.delete(questionKey);
                            return next;
                        });
                    }
                    // Optimistically reflect the answer in the completion gate so the
                    // signer can be marked complete without a refetch. Clearing every
                    // option, or emptying a text field, removes it again. Skip the update
                    // when membership is unchanged so overlay typing does not re-render
                    // the host on every keystroke.
                    setLocallySavedDataIds((previous) => {
                        const already = previous.has(questionKey);
                        if (answered === already) {
                            return previous;
                        }
                        const next = new Set(previous);
                        if (answered) {
                            next.add(questionKey);
                        }
                        else {
                            next.delete(questionKey);
                        }
                        return next;
                    });
                    if (clearsText && !everSent) {
                        textPersistFlushRef.current.delete(questionKey);
                        if (textPersistFlushRef.current.size === 0) {
                            setTextCommitHeld(false);
                        }
                        else if (!signaturePromptOpenRef.current) {
                            persistDebouncedTexts();
                        }
                        return;
                    }
                    const queueSetDataWrite = () => {
                        if (isCompleteRef.current) {
                            return Promise.resolve();
                        }
                        // Persist without refetching so the iframe isn't rebuilt on every
                        // toggle — the bundle keeps its own in-memory answer state and
                        // the saved-answer seed is only re-read on next open / completion.
                        const seq = (dataWriteSeqRef.current.get(questionKey) ?? 0) + 1;
                        dataWriteSeqRef.current.set(questionKey, seq);
                        const previousWrite = dataWriteChainRef.current.get(questionKey) ?? Promise.resolve();
                        const request = previousWrite.then(() => apiClient.post('/routes/econsent/set_data', {
                            ec__signed_document: documentDetail._id,
                            ec__identifier: questionKey,
                            type,
                            ec__value: clearsText ? '' : isTextInput ? textValue : isDate ? stringValue : values,
                        }));
                        dataWriteChainRef.current.set(questionKey, request.catch(() => undefined));
                        // A write that a newer one for the same key has already replaced is not a failure worth
                        // acting on: the newer write owns the value now. Both consumers below read this promise,
                        // so neither reverts the answer nor blocks completion over a superseded write.
                        const persist = request.catch((error) => {
                            if (dataWriteSeqRef.current.get(questionKey) !== seq) {
                                return undefined;
                            }
                            throw error;
                        });
                        // Keep it in the drain list so a rejected persist can abort
                        // auto-complete. The UI catch must not wrap that promise.
                        pendingSetDataRef.current.push(persist);
                        void persist.then(() => {
                            unsavedDataIdsRef.current.delete(questionKey);
                            if (answered) {
                                confirmedDataIdsRef.current.add(questionKey);
                                if (isTextInput || isDate) {
                                    confirmedValuesRef.current.set(questionKey, isTextInput ? textValue : stringValue);
                                }
                            }
                            else {
                                confirmedDataIdsRef.current.delete(questionKey);
                                confirmedValuesRef.current.delete(questionKey);
                            }
                        }, (error) => {
                            // Cortex already completed this signer (an earlier write committed their last
                            // required item, or a completion PUT whose response was lost landed), so this value
                            // was never saved. Show the record instead and never ask Cortex to complete again.
                            if (isCannotUpdateCompletedSignerError(error)) {
                                void latchServerCompletionRef.current();
                                return;
                            }
                            // Revert the optimistic add only when this key never landed.
                            // A later 500 (queue backup, or cannotUpdateCompletedSigner after
                            // Cortex auto-completed) would otherwise clear the sign gate
                            // while the overlay still shows the value a reload would restore.
                            if (isCompleteRef.current) {
                                return;
                            }
                            // Something typed since the clear is newer than what the clear replaced, and its own
                            // pending write decides what the record holds. Restoring the old value would
                            // overwrite it on screen and in the archive.
                            if (clearsText &&
                                (locallySavedTextsRef.current.has(questionKey) || textPersistFlushRef.current.has(questionKey))) {
                                return;
                            }
                            unsavedDataIdsRef.current.add(questionKey);
                            if (clearsText) {
                                // Cortex still holds the old value, so the field is answered again and the
                                // archive must keep stamping it.
                                setLocallyClearedDataIds((previous) => {
                                    if (!previous.has(questionKey)) {
                                        return previous;
                                    }
                                    const next = new Set(previous);
                                    next.delete(questionKey);
                                    return next;
                                });
                                if (textBeforeClear !== undefined) {
                                    locallySavedTextsRef.current.set(questionKey, textBeforeClear);
                                }
                                if (confirmedBeforeClear) {
                                    confirmedDataIdsRef.current.add(questionKey);
                                }
                                if (answeredBeforeClear) {
                                    setLocallySavedDataIds((previous) => previous.has(questionKey) ? previous : new Set(previous).add(questionKey));
                                }
                                addNotification({
                                    title: '',
                                    subtitle: translate('error.participant_documents.sign_failed'),
                                    type: 'error',
                                });
                                return;
                            }
                            if (confirmedDataIdsRef.current.has(questionKey)) {
                                // The field stays answered with what Cortex last accepted, so the screen, the
                                // archive, and Download/Print must show that, not the value that just failed.
                                // A newer value still waiting to be sent owns the field instead.
                                const confirmedValue = confirmedValuesRef.current.get(questionKey);
                                if (confirmedValue !== undefined && !textPersistFlushRef.current.has(questionKey)) {
                                    (isTextInput ? locallySavedTextsRef : locallySavedDatesRef).current.set(questionKey, confirmedValue);
                                }
                                addNotification({
                                    title: '',
                                    subtitle: translate('error.participant_documents.sign_failed'),
                                    type: 'error',
                                });
                                return;
                            }
                            setLocallySavedDataIds((previous) => {
                                const next = new Set(previous);
                                next.delete(questionKey);
                                return next;
                            });
                            locallySavedTextsRef.current.delete(questionKey);
                            locallySavedDatesRef.current.delete(questionKey);
                            addNotification({
                                title: '',
                                subtitle: translate('error.participant_documents.sign_failed'),
                                type: 'error',
                            });
                        });
                        return persist;
                    };
                    const isRequiredDatum = !!datum && !datum.ec__optional;
                    if (isTextInput) {
                        textPersistFlushRef.current.set(questionKey, {
                            write: queueSetDataWrite,
                            answered,
                            required: isRequiredDatum,
                        });
                        if (!signaturePromptOpenRef.current) {
                            persistDebouncedTexts();
                        }
                    }
                    else if (answered &&
                        isRequiredDatum &&
                        invalidTextIdsRef.current.size > 0 &&
                        outstandingRequiredRef.current.keys.every((key) => key === questionKey)) {
                        // Cortex would complete the signer on this write, around a text value it cannot save.
                        // Keep the answer waiting until the text is fixed and the signer presses Done.
                        textPersistFlushRef.current.set(questionKey, { write: queueSetDataWrite, answered, required: true });
                        warnInvalidTextAndHighlightRef.current();
                    }
                    else {
                        // A text edit still in the debounce goes first: if this write completes the signer,
                        // Cortex refuses everything sent after it. A held text write that would itself complete
                        // the signer still waits for Done.
                        let persists = [];
                        if (!textFlushWouldCompleteRef.current()) {
                            persistDebouncedTexts.cancel();
                            ({ persists } = flushPendingTextWrites());
                        }
                        const persist = persists.length
                            ? Promise.allSettled(persists).then(queueSetDataWrite)
                            : queueSetDataWrite();
                        if (answered && isRequiredDatum) {
                            void completeSignerIfReadyRef.current({ completedDataKey: questionKey, persist });
                        }
                    }
                }
                return;
            }
            if (data.kind === 'track' || data.kind === 'sign') {
                return;
            }
            if (data.kind !== 'request-name') {
                return;
            }
            // A terminally-voided document (voided, or cancelled — the sent-and-never-touched variant
            // of voiding) is read-only — deny every signature request rather than opening the sign
            // flow, regardless of whether the requested signature was previously pending.
            if (documentDetail && !isDocumentVoidable(documentDetail.ec__status)) {
                iframeRef.current?.contentWindow?.postMessage(buildEconsentBundleNameResponse(data.requestId, null), '*');
                return;
            }
            const requestedSignature = findSignatureByKey(documentDetail, data.request.ec__key);
            if (!requestedSignature) {
                iframeRef.current?.contentWindow?.postMessage(buildEconsentBundleNameResponse(data.requestId, null), '*');
                return;
            }
            if (signedSignatureIds.has(requestedSignature.ec__key)) {
                setSignatureRequest({
                    requestId: data.requestId,
                    request: {
                        ...data.request,
                        ec__key: requestedSignature.ec__key,
                        role: requestedSignature.ec__signer_role,
                    },
                });
                setSignatureModalMode('already-signed');
                return;
            }
            if (!activeSignerRole || (data.request?.role && !roleMatches(data.request.role, activeSignerRole))) {
                iframeRef.current?.contentWindow?.postMessage(buildEconsentBundleNameResponse(data.requestId, null), '*');
                return;
            }
            // A signer whose part is already complete has nothing left to sign — their optional fields
            // included. `signDocument` rejects those with `cannotUpdateCompletedSigner`, so opening the
            // credential prompt only buys a failure at the end of a finished session (SSP-4433). Same
            // completed-signer state that makes their choice fields read-only (`answerMetadata`), read
            // through the ref because this listener's closure predates the latch completion sets. Placed
            // after the already-signed branch so a completed signer can still view what they signed.
            if (isCompleteRef.current) {
                iframeRef.current?.contentWindow?.postMessage(buildEconsentBundleNameResponse(data.requestId, null), '*');
                return;
            }
            if (turnLocked) {
                iframeRef.current?.contentWindow?.postMessage(buildEconsentBundleNameResponse(data.requestId, null), '*');
                addNotification({
                    title: '',
                    subtitle: signingBlockedMessage,
                    type: 'error',
                });
                return;
            }
            // A text field showing a format error has no answer on record, and signing would complete the
            // document without the value the signer can see. Point them at it instead.
            if (invalidTextIdsRef.current.size > 0) {
                iframeRef.current?.contentWindow?.postMessage(buildEconsentBundleNameResponse(data.requestId, null), '*');
                warnInvalidTextAndHighlightRef.current();
                return;
            }
            // Block signing until every required choice for this signer is
            // answered (parity with React Native's "Done" gate) — signing is the
            // completion step here, so we can't let it finish with pending data
            // (SSP-4107).
            if (hasPendingRequiredDataRef.current) {
                iframeRef.current?.contentWindow?.postMessage(buildEconsentBundleNameResponse(data.requestId, null), '*');
                addNotification({
                    title: '',
                    subtitle: translate('error.participant_documents.required_data_pending'),
                    type: 'error',
                });
                return;
            }
            persistDebouncedTexts.cancel();
            signaturePromptOpenRef.current = true;
            setSignatureRequest({
                requestId: data.requestId,
                request: data.request,
            });
            setSignatureModalMode('sign');
        };
        handleHostMessageRef.current = handleHostMessage;
        const handleMessage = (event) => {
            if (event.source !== iframeRef.current?.contentWindow || !isEconsentBundleHostMessage(event.data)) {
                return;
            }
            handleHostMessage(event.data);
        };
        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [
        activeSignerRole,
        documentDetail,
        signedSignatureIds,
        requestedDataRoleByKey,
        signingBlockedMessage,
        turnLocked,
        persistDebouncedTexts,
    ]);
    const signingState = useMemo(() => {
        const completedDataIds = getCompletedDataIdentifiers(documentDetail);
        // Fold in answers persisted this session (no refetch) so required
        // choices toggled just now count toward completion (SSP-4107).
        for (const identifier of locallySavedDataIds) {
            completedDataIds.add(identifier);
        }
        for (const identifier of locallyClearedDataIds) {
            completedDataIds.delete(identifier);
        }
        const signerRole = activeSignerRole;
        const signatures = getRuntimeSignatures(documentDetail).filter((signature) => roleMatches(signature.ec__signer_role, signerRole));
        const dataRequirements = getRuntimeData(documentDetail).filter((datum) => roleMatches(datum.ec__signer_role, signerRole));
        const pendingSignatures = signatures.filter((signature) => !signedSignatureIds.has(signature.ec__key));
        const pendingRequiredSignatures = pendingSignatures.filter((signature) => !signature.ec__optional);
        const pendingRequiredData = dataRequirements.filter((datum) => !datum.ec__optional && !completedDataIds.has(datum.ec__key));
        const requestedKey = signatureRequest?.request.ec__key;
        return {
            pendingSignatures,
            pendingRequiredData,
            pendingRequiredSignatures,
            requiresInitials: !!requestedKey && !!findSignatureByKey(documentDetail, requestedKey)?.ec__initials,
        };
    }, [
        activeSignerRole,
        documentDetail,
        signedSignatureIds,
        locallySavedDataIds,
        locallyClearedDataIds,
        signatureRequest,
    ]);
    useEffect(() => {
        if (!signingState.requiresInitials) {
            return;
        }
        setInitials((current) => current || defaultInitials);
    }, [signingState.requiresInitials, defaultInitials]);
    hasPendingRequiredDataRef.current = signingState.pendingRequiredData.length > 0;
    const isComplete = completedThisSession || currentSigner?.ec__status === 'complete' || documentDetail?.ec__status === 'complete';
    // A terminally-voided document — either `voided` (some/all signing done) or `cancelled` (the
    // sent-and-never-touched variant, per the backend's `voidDocument` split) — must be read-only.
    // Derived from isDocumentVoidable rather than a literal so both statuses are covered.
    const isTerminallyVoided = !!documentDetail && !isDocumentVoidable(documentDetail.ec__status);
    // Withheld, not disabled: a visible Cancel/Close is a navigate-away control
    // while required signatures or data remain uncommitted.
    const hideNavigateAway = !isComplete &&
        !isTerminallyVoided &&
        (signingState.pendingRequiredSignatures.length > 0 || signingState.pendingRequiredData.length > 0);
    hideNavigateAwayRef.current = hideNavigateAway;
    isCompleteRef.current = isComplete;
    outstandingRequiredRef.current = {
        keys: [
            ...signingState.pendingRequiredSignatures.map((signature) => signature.ec__key),
            ...signingState.pendingRequiredData.map((datum) => datum.ec__key),
        ],
        labels: [
            ...signingState.pendingRequiredSignatures.map((signature) => signature.ec__title || signature.ec__label || signature.ec__description || signature.ec__key),
            ...signingState.pendingRequiredData.map((datum) => datum.ec__title || datum.ec__description || datum.ec__key),
        ],
    };
    textFlushWouldCompleteRef.current = () => {
        if (signingState.pendingRequiredSignatures.length > 0 || signingState.pendingRequiredData.length > 0) {
            return false;
        }
        const requiredKeys = new Set(getRuntimeData(documentDetail)
            .filter((datum) => !datum.ec__optional && roleMatches(datum.ec__signer_role, activeSignerRole))
            .map((datum) => datum.ec__key));
        return [...textPersistFlushRef.current].some(([key, { answered }]) => answered && requiredKeys.has(key));
    };
    const warnAndHighlight = ({ keys, labels }) => {
        addNotification({
            title: '',
            subtitle: translate('error.participant_documents.outstanding_required', {
                elements: labels.join(', '),
            }),
            type: 'error',
        });
        iframeRef.current?.contentWindow?.postMessage(buildEconsentBundleHighlightMessage(keys), '*');
    };
    const warnOutstandingAndHighlight = () => warnAndHighlight(outstandingRequiredRef.current);
    warnOutstandingAndHighlightRef.current = warnOutstandingAndHighlight;
    const warnFieldsAndHighlight = (keys) => {
        const data = getRuntimeData(documentDetail);
        warnAndHighlight({
            keys,
            labels: keys.map((key) => {
                const datum = data.find((entry) => entry.ec__key === key);
                return datum?.ec__title || datum?.ec__description || key;
            }),
        });
    };
    warnInvalidTextAndHighlightRef.current = () => warnFieldsAndHighlight([...invalidTextIdsRef.current]);
    const warnUnsavedAndHighlight = () => {
        if (unsavedDataIdsRef.current.size > 0) {
            warnFieldsAndHighlight([...unsavedDataIdsRef.current]);
        }
    };
    const archiveSignedPdfWhenComplete = (initial) => {
        if (archiveInFlightRef.current) {
            return;
        }
        archiveInFlightRef.current = true;
        void waitForCompleteDocument(async () => {
            const result = await refetchDocumentDetail();
            if (result.status === 'success' && result.data) {
                return result.data;
            }
            return undefined;
        }, { initial }).then((detail) => {
            if (detail?.ec__status === 'complete') {
                return generateAndUploadSignedPdf(detail);
            }
            archiveInFlightRef.current = false;
            if (!detail) {
                addNotification({
                    title: '',
                    subtitle: translate('error.participant_documents.load_failed'),
                    type: 'error',
                });
            }
            return undefined;
        });
    };
    function respondToSignatureRequest(name) {
        if (!signatureRequest || !iframeRef.current?.contentWindow) {
            return;
        }
        iframeRef.current.contentWindow.postMessage({
            ...buildEconsentBundleNameResponse(signatureRequest.requestId, name),
        }, '*');
    }
    const completeSignerIfReady = async (opts) => {
        if (!documentDetail ||
            isCompleteRef.current ||
            completeSignerInFlightRef.current ||
            turnLocked ||
            invalidTextIdsRef.current.size > 0) {
            return;
        }
        const remainingRequiredData = signingState.pendingRequiredData.filter((datum) => datum.ec__key !== opts?.completedDataKey);
        if (remainingRequiredData.length) {
            return;
        }
        const remainingRequiredSignatures = signingState.pendingRequiredSignatures.filter((signature) => signature.ec__key !== opts?.signedSignatureKey);
        if (remainingRequiredSignatures.length) {
            return;
        }
        completeSignerInFlightRef.current = true;
        const releaseCompletion = () => {
            if (isCompleteRef.current) {
                return;
            }
            releasedCompletionRef.current = { archive: !opts?.signedSignatureKey };
            completeSignerInFlightRef.current = false;
            setIsCompleting(false);
            if (completionCommittingRef.current) {
                completionCommittingRef.current = false;
                iframeRef.current?.contentWindow?.postMessage(buildEconsentBundleLockMessage(false), '*');
                // The overlays still show what was typed while the record was locking; save it now so the
                // screen and the record agree again.
                const held = [...heldCommitEditsRef.current.values()];
                heldCommitEditsRef.current.clear();
                for (const message of held) {
                    handleHostMessageRef.current(message);
                }
            }
        };
        try {
            if (opts?.persist) {
                try {
                    await opts.persist;
                }
                catch {
                    releaseCompletion();
                    warnUnsavedAndHighlight();
                    return;
                }
            }
            // Typing may still be sitting in the debounce window, and a quick correction can arrive
            // while the answer above is still saving. Persist all of it, draining until quiet.
            if (!(await drainPendingSetDataWrites())) {
                releaseCompletion();
                warnUnsavedAndHighlight();
                return;
            }
            if (isCompleteRef.current) {
                return;
            }
            // Nothing is awaited between the drain going quiet and this point, so from here on any new
            // answer could only land after the record is locked: the host ignores it and the overlays
            // stop taking input.
            completionCommittingRef.current = true;
            setIsCompleting(true);
            iframeRef.current?.contentWindow?.postMessage(buildEconsentBundleLockMessage(true), '*');
            await apiClient
                .put('/routes/econsent/set_signer_complete', {
                ec__signed_document: documentDetail._id,
            })
                .catch((error) => {
                // Cortex refuses only because the signer is complete already, which is what was asked.
                if (!isCannotUpdateCompletedSignerError(error)) {
                    throw error;
                }
            });
            // Stay latched. The choice path does not refetch, so isComplete would
            // otherwise stay false and a later toggle would PUT again.
            isCompleteRef.current = true;
            setCompletedThisSession(true);
            setIsCompleting(false);
            if (heldCommitEditsRef.current.size > 0) {
                heldCommitEditsRef.current.clear();
                showLateEditNotice();
            }
            // Archive here too when the signer completed on an answer rather than a signature. The
            // signature path runs its own upload once its refetch has the new signature on the record;
            // this path had none, so a document whose last required element is a date — or which asks
            // for no signature at all — completed with no stored archive while Download and Print still
            // stamped a copy of the same record. Poll until Cortex flips complete: one refetch can still
            // see partial while checkDocumentComplete is in flight.
            if (!opts?.signedSignatureKey) {
                archiveSignedPdfWhenComplete();
            }
        }
        catch {
            releaseCompletion();
        }
    };
    completeSignerIfReadyRef.current = completeSignerIfReady;
    /**
     * The document as Cortex now holds it, or `undefined` when two refetches fail. A failed refetch
     * still hands back the cached document, which is missing what was just saved, so it must never
     * be archived.
     */
    const refetchRecord = async () => {
        for (let attempt = 0; attempt < 2; attempt += 1) {
            const result = await refetchDocumentDetail();
            if (result.status === 'success' && result.data) {
                return result.data;
            }
        }
        addNotification({ title: '', subtitle: translate('error.participant_documents.load_failed'), type: 'error' });
        return undefined;
    };
    const latchServerCompletion = async () => {
        if (!documentDetail || isCompleteRef.current) {
            return;
        }
        isCompleteRef.current = true;
        completionCommittingRef.current = false;
        setCompletedThisSession(true);
        // Cortex will refuse anything still unsent too.
        persistDebouncedTexts.cancel();
        textPersistFlushRef.current.clear();
        setTextCommitHeld(false);
        heldCommitEditsRef.current.clear();
        showLateEditNotice();
        // A signing flow archives on its own; otherwise nothing else will, since the completion here
        // was Cortex's, not a set_signer_complete this session sent.
        const archive = releasedCompletionRef.current
            ? releasedCompletionRef.current.archive
            : !signaturePromptOpenRef.current;
        const refreshedDetail = await refetchRecord();
        if (!refreshedDetail) {
            return;
        }
        // What this session typed but Cortex refused is not on the record: the screen and the archive
        // show what the refetch returned, not the local copies.
        locallySavedTextsRef.current.clear();
        locallySavedDatesRef.current.clear();
        setLocallyClearedDataIds(new Set());
        if (archive) {
            archiveSignedPdfWhenComplete(refreshedDetail);
        }
    };
    latchServerCompletionRef.current = latchServerCompletion;
    /** Done: save the held text and complete the signer, as the debounce would have. */
    const commitHeldTexts = async () => {
        if (invalidTextIdsRef.current.size > 0) {
            warnInvalidTextAndHighlightRef.current();
            return false;
        }
        persistDebouncedTexts.cancel();
        const { lastKey, lastPersist } = flushPendingTextWrites();
        await completeSignerIfReady({ completedDataKey: lastKey, persist: lastPersist });
        return unsavedDataIdsRef.current.size === 0;
    };
    // Leaving with held text finishes the signer the way Done does. The answer is complete on screen,
    // and only saving it would leave every required item recorded but the signer never completed and
    // the document never archived.
    const leave = () => {
        if (!textCommitHeld || isCompleteRef.current) {
            onClose();
            return;
        }
        void commitHeldTexts().then((committed) => {
            if (committed) {
                onClose();
            }
        });
    };
    leaveRef.current = leave;
    const signSelectedSignature = async () => {
        if (!documentDetail || !activeSignerRole || !signatureRequest) {
            addNotification({
                title: '',
                subtitle: translate('error.participant_documents.not_active_signer'),
                type: 'error',
            });
            return;
        }
        if (!isDocumentVoidable(documentDetail.ec__status)) {
            addNotification({
                title: '',
                subtitle: translate('info.participant_documents.document_voided'),
                type: 'error',
            });
            return;
        }
        const signature = findSignatureByKey(documentDetail, signatureRequest.request.ec__key);
        if (!signature) {
            addNotification({
                title: '',
                subtitle: translate('error.participant_documents.signature_field_not_found'),
                type: 'error',
            });
            return;
        }
        if (signedSignatureIds.has(signature.ec__key)) {
            respondToSignatureRequest(null);
            setSignatureRequest(null);
            addNotification({
                title: '',
                subtitle: translate('error.participant_documents.signature_already_captured'),
                type: 'error',
            });
            return;
        }
        setIsSubmitting(true);
        try {
            // Sign completes the signer on the server when every required overlay is
            // already stored. Persist the latest typed value first so the archive
            // and the next viewer do not keep a truncated in-flight keystroke.
            if (!(await drainPendingSetDataWrites())) {
                closeSignatureModal();
                warnUnsavedAndHighlight();
                return;
            }
            const signedDate = new Date().toISOString();
            await apiClient.post('/routes/econsent/sign', {
                ec__signed_document: documentDetail._id,
                username: accountEmail,
                password,
                ec__signature_identifier: signature.ec__key,
                signed_initials: signature.ec__initials ? initials.trim() : undefined,
                signed_name: printedName,
                signed_date: signedDate,
                ...getDeviceClockAttestation(),
            });
            const shouldCompleteSigner = !signingState.pendingRequiredData.length &&
                signingState.pendingRequiredSignatures.every((requiredSignature) => requiredSignature.ec__key === signature.ec__key);
            await completeSignerIfReady({ signedSignatureKey: signature.ec__key });
            invalidateSignedDocumentQueries(queryClient, {
                participantId,
                documentId: documentDetail._id,
            });
            const { data: refreshedDetail } = await refetchDocumentDetail();
            respondToSignatureRequest(signature.ec__initials ? initials.trim() : printedName);
            setSignatureRequest(null);
            setPassword('');
            setInitials('');
            addNotification({
                title: '',
                subtitle: translate('info.participant_documents.signature_captured'),
                type: 'success',
            });
            if (shouldCompleteSigner) {
                archiveSignedPdfWhenComplete(refreshedDetail);
                onClose();
            }
        }
        catch (error) {
            addNotification({
                title: '',
                subtitle: getApiErrorMessage?.(error, true) ?? translate('error.participant_documents.sign_failed'),
                type: 'error',
            });
        }
        finally {
            setIsSubmitting(false);
        }
    };
    if (!document || !isDocGenEconsentEnabled) {
        return null;
    }
    const isAlreadySignedModal = signatureModalMode === 'already-signed';
    const documentTitle = getDisplayTitle(documentDetail?.ec__document_template?.ec__title || document.ec__document_template.ec__title);
    const canSubmit = !!activeSignerRole &&
        !isComplete &&
        !isTerminallyVoided &&
        !!printedName &&
        !!password &&
        (!signingState.requiresInitials || !!initials.trim()) &&
        !!signatureRequest;
    const showVoidButton = isDocGenEconsentEnabled && !!onVoidDocument && !!canVoidDocument?.(document);
    // Unsigned required work: withhold Download/Print. Cancel stays so Nucleus
    // is not buttonless (it also hides the header ✕ and no-ops Escape).
    const canCopyConsentPdf = canDownloadConsentPdf && !!previewHtml && !isBundleProcessing && !isDocumentError && !hideNavigateAway;
    const closeOrCancelButton = {
        label: isDocGenEconsentEnabled && isComplete ? translate('generic.close') : translate('generic.cancel'),
        onClick: hideNavigateAway ? warnOutstandingAndHighlight : leave,
        disabled: isSubmitting || isCopyingPdf,
    };
    const voidButton = {
        label: translate('action.participant_documents.void_consent'),
        onClick: () => onVoidDocument?.(document),
        disabled: isSubmitting || isCopyingPdf,
        variant: 'secondary-destructive',
    };
    const printButton = {
        label: translate('action.participant_documents.print'),
        onClick: () => {
            void copyConsentPdf('print');
        },
        disabled: isSubmitting || isCopyingPdf,
        dataTestId: 'participant-document-print',
    };
    const downloadButton = {
        label: translate('action.participant_documents.download'),
        onClick: () => {
            void copyConsentPdf('download');
        },
        disabled: isSubmitting || isCopyingPdf,
        dataTestId: 'participant-document-download',
    };
    const doneButton = {
        label: translate('generic.done'),
        onClick: () => {
            void commitHeldTexts();
        },
        disabled: isSubmitting || isCopyingPdf || isCompleting,
        dataTestId: 'participant-document-done',
    };
    const footerSecondaryActionButtons = showVoidButton
        ? canCopyConsentPdf
            ? [closeOrCancelButton, voidButton, printButton]
            : [closeOrCancelButton, voidButton]
        : canCopyConsentPdf
            ? [closeOrCancelButton, printButton]
            : [closeOrCancelButton];
    return (_jsxs(_Fragment, { children: [_jsx(ModalWindow, { title: isDocGenEconsentEnabled
                    ? translate('label.participant_documents.consent_document')
                    : document.ec__document_template.ec__title, subtitle: isDocGenEconsentEnabled ? (_jsx(Text, { size: 'sm', className: 'truncate text-neutral-600', title: documentTitle, children: documentTitle })) : undefined, isOpen: isOpen, closeWindow: hideNavigateAway ? warnOutstandingAndHighlight : leave, width: 'lg', removeBodyPadding: true, dataTestId: 'participant-document-modal', footerSecondaryActionButtons: footerSecondaryActionButtons, footerPrimaryActionButton: textCommitHeld && !isComplete ? doneButton : canCopyConsentPdf ? downloadButton : undefined, children: _jsx("div", { className: `flex justify-center bg-neutral-50 p-4 ${isDocGenEconsentEnabled ? 'h-[calc(80vh-10.25rem)] min-h-0' : 'h-[78vh]'}`, "aria-busy": isCompleting || undefined, children: isDocumentLoading && !documentDetail ? (_jsx(Spinner, { wrapper: 'full' })) : isDocumentError ? (_jsx(Text, { className: 'text-red-600', children: translate('error.participant_documents.load_failed') })) : hasNoBundleSource ? (_jsxs("div", { className: 'flex flex-col items-center justify-center gap-3 text-center', "data-testid": 'participant-document-classic-unavailable', children: [_jsx(Text, { children: translate('error.participant_documents.classic_unavailable') }), classicDocumentUrl ? (_jsx(Link, { href: classicDocumentUrl, target: '_blank', rel: 'noopener noreferrer', "data-testid": 'participant-document-open-classic', children: translate('action.create_consent.open_in_classic') })) : null] })) : isBundleProcessingTimedOut ? (_jsxs("div", { className: 'flex flex-col items-center justify-center gap-3 text-center', children: [isDelayedLocked ? (_jsx(Text, { className: 'px-4 py-2 text-sm text-neutral-700', dataTestId: 'participant-document-signing-blocked', children: signingBlockedMessage })) : null, _jsx(Text, { className: 'text-red-600', children: translate('error.participant_documents.bundle_processing_timeout') }), _jsx(Button, { variant: 'secondary', size: 'sm', label: translate('generic.retry'), onClick: () => void refetchDocumentDetail(), dataTestId: 'participant-document-bundle-retry' })] })) : isBundleProcessing ? (_jsxs("div", { className: 'flex flex-col items-center justify-center gap-3 text-center text-neutral-600', children: [isDelayedLocked ? (_jsx(Text, { className: 'px-4 py-2 text-sm text-neutral-700', dataTestId: 'participant-document-signing-blocked', children: signingBlockedMessage })) : null, _jsx(Spinner, {}), _jsx(Text, { children: translate('info.participant_documents.bundle_processing') })] })) : (_jsxs("div", { className: `relative h-full w-full max-w-[760px] overflow-hidden rounded border border-neutral-300 bg-white ${isDocGenEconsentEnabled ? 'min-h-0' : ''}`, children: [turnLocked && (_jsx(Text, { className: 'px-4 py-2 text-sm text-neutral-700', dataTestId: 'participant-document-signing-blocked', children: signingBlockedMessage })), previewHtml ? (_jsx("iframe", { ref: iframeRef, srcDoc: previewHtml, sandbox: 'allow-scripts', title: documentTitle, className: 'h-full w-full border-0', "data-testid": 'participant-document-preview' })) : previewError ? (_jsx("div", { className: 'p-6 text-center text-red-600', children: previewError })) : (_jsx(Spinner, { wrapper: 'full' }))] })) }) }), _jsx(ModalWindow, { title: isAlreadySignedModal
                    ? translate('label.participant_documents.signature_status')
                    : isDocGenEconsentEnabled
                        ? translate('label.participant_documents.sign_consent')
                        : document.ec__document_template.ec__title, subtitle: isDocGenEconsentEnabled && !isAlreadySignedModal ? (_jsx(Text, { size: 'sm', className: 'truncate text-neutral-600', title: documentTitle, children: documentTitle })) : undefined, isOpen: !!signatureRequest, closeWindow: closeSignatureModal, width: isAlreadySignedModal ? 'base' : 'lg', dataTestId: 'participant-document-signature-modal', footerSecondaryActionButtons: [
                    {
                        label: isAlreadySignedModal ? translate('generic.close') : translate('generic.cancel'),
                        onClick: closeSignatureModal,
                        disabled: isSubmitting,
                    },
                ], footerPrimaryActionButton: isAlreadySignedModal
                    ? undefined
                    : {
                        label: isSubmitting
                            ? translate('action.participant_documents.signing')
                            : translate('action.participant_documents.sign'),
                        onClick: signSelectedSignature,
                        variant: 'primary',
                        disabled: !canSubmit || isSubmitting,
                    }, children: _jsx("div", { className: `flex justify-center ${isAlreadySignedModal ? 'py-2' : 'pb-6 pt-8'}`, children: _jsxs("div", { className: 'flex w-full max-w-[560px] flex-col gap-6', children: [isAlreadySignedModal ? (_jsxs("div", { className: 'flex flex-col gap-2', children: [_jsx(Text, { className: 'text-lg font-semibold text-neutral-900', children: translate('label.participant_documents.signature_already_captured') }), _jsx(Text, { className: 'text-sm text-neutral-600', children: translate('info.participant_documents.signature_already_completed') })] })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: 'flex flex-col gap-2', children: [_jsx(Text, { className: 'text-2xl font-semibold text-neutral-900', children: translate('label.participant_documents.sign_confirmation_heading') }), _jsx(Text, { className: 'text-lg text-neutral-600', children: translate('label.participant_documents.sign_confirmation') })] }), _jsx(Input, { id: 'participant-document-printed-name', label: translate('label.participant_documents.printed_name'), value: printedName, onChange: (event) => setPrintedName(event.target.value), disabled: isSubmitting || isComplete || isTerminallyVoided, width: 'full', required: true }), _jsx(Input, { id: 'participant-document-email', label: translate('generic.email'), value: accountEmail, disabled: true, width: 'full' }), signingState.requiresInitials && (_jsx(Input, { id: 'participant-document-initials', label: translate('label.participant_documents.initials'), value: initials, onChange: (event) => setInitials(event.target.value), disabled: isSubmitting || isComplete || isTerminallyVoided, width: 'full', required: true })), _jsxs("div", { className: 'flex flex-col gap-2', children: [_jsxs("div", { className: 'flex items-center justify-between', children: [_jsx(Text, { className: 'text-sm font-medium text-neutral-900', children: translate('generic.password') }), _jsx("button", { className: 'text-sm font-medium text-primary-600 underline', type: 'button', children: translate('generic.forgot_password') })] }), _jsx(Input, { id: 'participant-document-password', label: '', type: 'password', value: password, onChange: (event) => setPassword(event.target.value), disabled: isSubmitting || isComplete || isTerminallyVoided, width: 'full', required: true })] })] })), !activeSignerRole && (_jsx(Text, { className: 'text-red-600', children: translate('error.participant_documents.not_active_signer') })), !isAlreadySignedModal && !!signingState.pendingRequiredData.length && (_jsx(Text, { className: 'text-red-600', children: translate('error.participant_documents.required_data_pending') })), !isAlreadySignedModal && isComplete && (_jsx(Text, { children: translate('info.participant_documents.document_completed') })), !isAlreadySignedModal && isTerminallyVoided && (_jsx(Text, { className: 'text-red-600', children: translate('info.participant_documents.document_voided') }))] }) }) })] }));
};
